import { createSlice } from '@reduxjs/toolkit'
import name from './name'
import Helpers from './helpers'

export const SectionMap = {
  details: {
    title: 'Details'
  },
  build: {
    title: 'Build'
  },
  email: {
    title: 'Email'
  },
  preview: {
    title: 'Preview'
  }
}

export const skillBiteEdit = createSlice({
  name,
  initialState: {
    skillBite: {},
    selectedSessionId: null,
    selectedSectionId: null,
    selectedPageId: null,
    editMode: false
  },
  reducers: {
    getSkillBite: (state,
      { payload }) => {
      state.skillBite = payload
      state.editMode = !payload.published
      state.selectedSessionId = payload.sessions.length ? payload.sessions[0].id : null
      state.selectedSectionId = payload.sessions.length && payload.sessions[0].sections.length ? payload.sessions[0].sections[0].id : null
      state.selectedPageId = payload.sessions.length && payload.sessions[0].sections.length && payload.sessions[0].sections[0].pages.length ? payload.sessions[0].sections[0].pages[0].id : null
    },
    updateSkillBiteDetails: (state, { payload }) => {
      Helpers.updateSkillBite(state, {
        updateFunc: (skillBite) => {
          return { ...skillBite, ...payload }
        }
      })
    },
    addSession: (state, { payload }) => {
      Helpers.updateSkillBite(state, {
        updateFunc: (skillBite) => {
          return { ...skillBite, sessions: [...skillBite.sessions, payload] }
        }
      })
    },
    updateSession: (state, { payload }) => {
      const { name, description } = payload

      Helpers.updateSession(state, {
        sessionId: payload.id,
        updateFunc: (session) => {
          return { ...session, name, description }
        }
      })
    },
    updateSessionEmail: (state, { payload }) => {
      const { emailConfig } = payload

      Helpers.updateSession(state, {
        sessionId: payload.id,
        updateFunc: (session) => {
          return { ...session, emailConfig }
        }
      })
    },
    addSection: (state, { payload }) => {
      const { sessionId, section } = payload

      Helpers.updateSession(state, {
        sessionId,
        updateFunc: (session) => {
          return { ...session, sections: [...session.sections, section] }
        }
      })

      if (state.selectedSessionId === sessionId) {
        state.selectedSectionId = section.id
      }
    },
    renameSection: (state, { payload }) => {
      const { sessionId, sectionId, section: renamedSection } = payload

      Helpers.updateSection(state, {
        sessionId,
        sectionId,
        updateFunc: (section) => {
          return { ...section, ...renamedSection }
        }
      })
    },
    addPage: (state, { payload }) => {
      const { sessionId, sectionId, page } = payload

      Helpers.updateSection(state, {
        sessionId,
        sectionId,
        updateFunc: (section) => {
          return { ...section, pages: [...section.pages, page] }
        }
      })

      state.selectedPageId = page.id
    },
    deletePage: (state, { payload }) => {
      const { sessionId, sectionId, pageId } = payload

      Helpers.updateSection(state, {
        sessionId,
        sectionId,
        updateFunc: (section) => {
          return { ...section, pages: section.pages.filter((page) => page.id !== pageId) }
        }
      })

      if (state.selectedPageId === pageId) {
        state.selectedPageId = null
      }
    },
    deleteSection: (state, { payload }) => {
      const { sessionId, sectionId } = payload
      Helpers.updateSession(state, {
        sessionId,
        updateFunc: (session) => {
          return { ...session, sections: session.sections.filter((section) => section.id !== sectionId) }
        }
      })

      if (state.selectedSectionId === sectionId) {
        state.selectedSectionId = null
        state.selectedPageId = null
      }
    },
    deleteSession: (state, { payload }) => {
      const { sessionId } = payload

      Helpers.updateSkillBite(state, {
        updateFunc: (skillBite) => {
          return { ...skillBite, sessions: skillBite.sessions.filter((session) => session.id !== sessionId) }
        }
      })

      if (state.selectedSessionId === sessionId) {
        state.selectedSessionId = null
        state.selectedSectionId = null
        state.selectedPageId = null
      }
    },
    addBlock: (state, { payload }) => {
      const { block, sessionId, sectionId, pageId } = payload

      Helpers.updatePage(state, {
        sessionId,
        sectionId,
        pageId,
        updateFunc: (page) => {
          return { ...page, blocks: [...page.blocks, block] }
        }
      })
    },
    deleteBlock: (state, { payload }) => {
      const { sessionId, sectionId, pageId, blockId } = payload
      Helpers.updatePage(state, {
        sessionId,
        sectionId,
        pageId,
        updateFunc: (page) => {
          const blockToDelete = page.blocks.find((block) => block.id === blockId)
          return {
            ...page,
            blocks: page
              .blocks
              .filter((block) => block.id !== blockId)
              .map(block => ({
                ...block,
                order: blockToDelete && block.order > blockToDelete.order ? block.order - 1 : block.order
              }))
          }
        }
      })
    },
    updatePageBlocks: (state, { payload }) => {
      const { sessionId, pageId, sectionId, blocks } = payload
      Helpers.updatePage(state, {
        sessionId,
        sectionId,
        pageId,
        updateFunc: (page) => {
          return { ...page, blocks }
        }
      })
    },
    changeSelectedSession: (state, { payload }) => {
      if (payload.id === state.selectedSessionId) {
        state.selectedSessionId = null
        state.selectedSectionId = null
        state.selectedPageId = null
      } else {
        state.selectedSessionId = payload.id
        state.selectedSectionId = payload.sections.length > 0 ? payload.sections[0].id : null
        state.selectedPageId = payload.sections.length && payload.sections[0].pages.length ? payload.sections[0].pages[0].id : null
      }
    },
    changeSelectedSection: (state, { payload }) => {
      state.selectedSectionId = payload.id
      state.selectedPageId = payload.pages.length ? payload.pages[0].id : null
    },
    changeSelectedPage: (state, { payload }) => {
      state.selectedPageId = payload.id
    },
    publishSkillBite: (state, { payload }) => {
      Helpers.updateSkillBite(state, {
        updateFunc: (skillBite) => {
          return { ...skillBite, ...payload }
        }
      })
    },
    changeEditMode: (state, { payload }) => {
      state.editMode = payload
    },
    reset: (state) => {
      state.skillBite = {}
      state.editMode = false
      state.selectedSessionId = null
      state.selectedSectionId = null
      state.selectedPageId = null
    }
  }
})

export const {
  addSession,
  addSection,
  changeEditMode,
  renameSection,
  getSkillBite,
  updateSkillBiteDetails,
  updateSession,
  updateSessionEmail,
  addPage,
  deletePage,
  deleteSection,
  deleteSession,
  addBlock,
  deleteBlock,
  updatePageBlocks,
  changeSelectedPage,
  changeSelectedSession,
  changeSelectedSection,
  publishSkillBite,
  reset
} = skillBiteEdit.actions

export default skillBiteEdit.reducer
